import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';

export class SupabaseAdminService {
  constructor(private readonly client: SupabaseClient<Database>) {}

  async getAdminCount(): Promise<number> {
    try {
      const { data, error } = await this.client
        .from('user_admin_map_v')
        .select('user_id');
      if (error) return 0;
      return (data || []).length;
    } catch {
      return 0;
    }
  }

  async getTotalUsers(): Promise<number> {
    try {
      console.log('Fetching total users count...');

      // Prefer public view for counting users (bypasses RLS)
      const { count, error } = await this.client
        .from('profiles_public_v')
        .select('*', { count: 'exact', head: true });
      console.log('Profiles count result:', { count, error });
      if (!error && typeof count === 'number' && count > 0) return count;

      // Fallback strategy: count distinct users from group_members (public SELECT allowed by policy)
      const { data: members, error: gmError } = await this.client
        .from('group_members')
        .select('user_id');

      if (gmError) {
        console.error('Fallback group_members fetch failed:', gmError);
        return 0;
      }

      const distinct = new Set((members || []).map(m => (m as any).user_id));
      console.log('Distinct users from group_members:', distinct.size);
      return distinct.size;
    } catch (error) {
      console.error('Error in getTotalUsers:', error);
      return 0;
    }
  }

  async getGroupStats(): Promise<{ totalGroups: number; subgroups: number }> {
    try {
      const { data: groups, error } = await this.client
        .from('groups')
        .select('id, parent_id');

      if (error) {
        console.error('Error fetching groups:', error);
        return { totalGroups: 0, subgroups: 0 };
      }

      const totalGroups = groups?.length || 0;
      const subgroups = groups?.filter(g => g.parent_id !== null).length || 0;

      return { totalGroups, subgroups };
    } catch (error) {
      console.error('Error in getGroupStats:', error);
      return { totalGroups: 0, subgroups: 0 };
    }
  }

  async getTotalEvents(): Promise<number> {
    try {
      const { count, error } = await this.client
        .from('events')
        .select('*', { count: 'exact', head: true });

      if (error) {
        console.error('Error fetching event count:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error in getTotalEvents:', error);
      return 0;
    }
  }

  async getEventsThisMonth(): Promise<number> {
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const { count, error } = await this.client
        .from('events')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startOfMonth.toISOString())
        .lte('created_at', endOfMonth.toISOString());

      if (error) {
        console.error('Error fetching monthly events:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error in getEventsThisMonth:', error);
      return 0;
    }
  }

  async getUsersThisMonth(): Promise<number> {
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const { count, error } = await this.client
        .from('profiles_public_v')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startOfMonth.toISOString())
        .lte('created_at', endOfMonth.toISOString());

      if (error) {
        console.error('Error fetching monthly users:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error in getUsersThisMonth:', error);
      return 0;
    }
  }

  async getNewGroupsThisMonth(): Promise<number> {
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const { count, error } = await this.client
        .from('groups')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startOfMonth.toISOString())
        .lte('created_at', endOfMonth.toISOString());

      if (error) {
        console.error('Error fetching monthly groups:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error in getNewGroupsThisMonth:', error);
      return 0;
    }
  }

  async getContactStats(): Promise<{
    total: number;
    withEmail: number;
    withPhone: number;
    complete: number;
  }> {
    try {
      const { data, error } = await this.client
        .from('profiles_public_v')
        .select('email, phone');

      if (error) {
        console.error('Error fetching contact stats:', error);
        return { total: 0, withEmail: 0, withPhone: 0, complete: 0 };
      }

      let withEmail = 0;
      let withPhone = 0;
      let complete = 0;
      const rows = data || [];

      rows.forEach((row: any) => {
        const hasEmail = Boolean(row.email && row.email.trim());
        const hasPhone = Boolean(row.phone && row.phone.trim());
        if (hasEmail) withEmail += 1;
        if (hasPhone) withPhone += 1;
        if (hasEmail && hasPhone) complete += 1;
      });

      return {
        total: rows.length,
        withEmail,
        withPhone,
        complete,
      };
    } catch (error) {
      console.error('Error in getContactStats:', error);
      return { total: 0, withEmail: 0, withPhone: 0, complete: 0 };
    }
  }
}
